import { PUMP_AMM_PROGRAM_ID_PUBKEY, PumpAmmSdk, SellBaseInputResult, globalConfigPda } from '@pump-fun/pump-swap-sdk';
import {
    ASSOCIATED_TOKEN_PROGRAM_ID,
    NATIVE_MINT,
    TOKEN_PROGRAM_ID,
    createAssociatedTokenAccountIdempotentInstruction,
    getAssociatedTokenAddressSync,
} from '@solana/spl-token';
import {
    AccountMeta,
    ComputeBudgetProgram,
    Connection,
    Keypair,
    PublicKey,
    SystemProgram,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { getZeroslotTipInstruction, sendZeroslotTransaction } from '../0slot/0slot';
import { BN } from '@coral-xyz/anchor';
import { toast } from 'react-toastify';

export interface PumpswapPool {
    poolBump: number;
    index: number;
    creator: PublicKey;
    baseMint: PublicKey;
    quoteMint: PublicKey;
    lpMint: PublicKey;
    baseVault: PublicKey;
    quoteVault: PublicKey;
    lpSupply: BN;
}

export function decodePumpswapPool(data: Buffer): PumpswapPool {
    const OFF = {
        DISCRIM: 0,
        POOL_BUMP: 8,
        INDEX: 9,
        CREATOR: 11,
        BASE_MINT: 43,
        QUOTE_MINT: 75,
        LP_MINT: 107,
        BASE_VAULT: 139,
        QUOTE_VAULT: 171,
        LP_SUPPLY: 203,
    } as const;

    return {
        poolBump: data.readUInt8(OFF.POOL_BUMP),
        index: data.readUInt16LE(OFF.INDEX),
        creator: new PublicKey(data.slice(OFF.CREATOR, OFF.CREATOR + 32)),
        baseMint: new PublicKey(data.slice(OFF.BASE_MINT, OFF.BASE_MINT + 32)),
        quoteMint: new PublicKey(data.slice(OFF.QUOTE_MINT, OFF.QUOTE_MINT + 32)),
        lpMint: new PublicKey(data.slice(OFF.LP_MINT, OFF.LP_MINT + 32)),
        baseVault: new PublicKey(data.slice(OFF.BASE_VAULT, OFF.BASE_VAULT + 32)),
        quoteVault: new PublicKey(data.slice(OFF.QUOTE_VAULT, OFF.QUOTE_VAULT + 32)),
        lpSupply: new BN(data.slice(OFF.LP_SUPPLY, OFF.LP_SUPPLY + 8), 'le'),
    };
}

export const getPumpswapPoolId = async (connection: Connection, baseMint: PublicKey): Promise<PublicKey | null> => {
    const BASE_MINT_OFFSET = 43;
    const QUOTE_MINT_OFFSET = 75;
    const LP_SUPPLY_OFFSET = 203;
    const LP_SUPPLY_LENGTH = 8;

    const pools = await connection.getProgramAccounts(PUMP_AMM_PROGRAM_ID_PUBKEY, {
        filters: [
            { memcmp: { offset: BASE_MINT_OFFSET, bytes: baseMint.toBase58() } },
            { memcmp: { offset: QUOTE_MINT_OFFSET, bytes: NATIVE_MINT.toBase58() } },
        ],
    });

    if (!pools.length) return null;

    const decodedPools = pools.map((pool) => {
        const lpSupply = new BN(pool.account.data.slice(LP_SUPPLY_OFFSET, LP_SUPPLY_OFFSET + LP_SUPPLY_LENGTH), 'le');
        return { pubkey: pool.pubkey, lpSupply };
    });

    const maxPool = decodedPools.reduce((max, pool) => (pool.lpSupply.gt(max.lpSupply) ? pool : max));
    return maxPool.pubkey;
};

export async function fetchPumpswapReserves(connection: Connection, pool: PumpswapPool): Promise<{ baseReserves: BN; quoteReserves: BN }> {
    const accounts = await connection.getMultipleAccountsInfo([pool.baseVault, pool.quoteVault], 'processed');
    if (!accounts[0] || !accounts[1]) throw new Error('Vault account missing');
    const baseAmount = accounts[0].data.readBigUInt64LE(64);
    const quoteAmount = accounts[1].data.readBigUInt64LE(64);
    return {
        baseReserves: new BN(baseAmount.toString()),
        quoteReserves: new BN(quoteAmount.toString()),
    };
}

export async function buyPumpswapToken(
    connection: Connection,
    poolId: PublicKey,
    baseMint: PublicKey,
    quoteAmountIn: number,
    buyer: Keypair,
    zeroSlotTip: number,
    unitLimit: number = 150000,
    unitPrice: number = 400000,
) {
    const sdk = new PumpAmmSdk(connection);

    const swapSolanaState = await sdk.swapSolanaState(poolId, buyer.publicKey);
    const tx = new VersionedTransaction(
        new TransactionMessage({
            payerKey: buyer.publicKey,
            recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
            instructions: [
                ComputeBudgetProgram.setComputeUnitLimit({ units: unitLimit }),
                ComputeBudgetProgram.setComputeUnitPrice({ microLamports: unitPrice }),
                getZeroslotTipInstruction(buyer.publicKey, zeroSlotTip),

                createAssociatedTokenAccountIdempotentInstruction(
                    buyer.publicKey,
                    getAssociatedTokenAddressSync(baseMint, buyer.publicKey),
                    buyer.publicKey,
                    baseMint,
                ),
                ...(await sdk.buyQuoteInput(swapSolanaState, new BN(quoteAmountIn), 10)),
            ],
        }).compileToV0Message(),
    );
    tx.sign([buyer]);
    const hash = await sendZeroslotTransaction(tx);

    toast.success(`PUMPSWAP transaction sent: ${hash}`);

    return hash;
}

export async function getSellIxs(connection: Connection, seller: PublicKey, baseMint: PublicKey, baseAmountIn: BN, slippage: number = 10) {
    const poolId = await getPumpswapPoolId(connection, baseMint);
    if (!poolId) throw new Error('Pool not found');

    const sdk = new PumpAmmSdk(connection);
    const swapSolanaState = await sdk.swapSolanaState(poolId, seller);
    return sdk.sellBaseInput(swapSolanaState, baseAmountIn, slippage);
}

export function ceilDiv(a: BN, b: BN): BN {
    if (b.isZero()) {
        throw new Error('Cannot divide by zero.');
    }
    return a.add(b.subn(1)).div(b);
}

export function fee(amount: BN, basisPoints: BN): BN {
    return ceilDiv(amount.mul(basisPoints), new BN(10_000));
}

export function getSellBaseInput(base: BN, slippage: number, baseReserve: BN, quoteReserve: BN, lpFeeBps: BN, protocolFeeBps: BN) {
    if (base.isZero()) {
        return new BN(0);
    }
    if (baseReserve.isZero() || quoteReserve.isZero()) {
        throw new Error("Invalid input: 'baseReserve' or 'quoteReserve' cannot be zero.");
    }
    if (lpFeeBps.isNeg() || protocolFeeBps.isNeg()) {
        throw new Error('Fee basis points cannot be negative.');
    }

    console.log('reserves:', { baseReserve: baseReserve.toString(), quoteReserve: quoteReserve.toString() });
    const quoteAmountOut = quoteReserve.mul(base).div(baseReserve.add(base));

    const lpFee = fee(quoteAmountOut, lpFeeBps);
    const protocolFee = fee(quoteAmountOut, protocolFeeBps);

    const finalQuote = quoteAmountOut.sub(lpFee).sub(protocolFee);
    if (finalQuote.isNeg()) {
        throw new Error('Fees exceed total output; final quote is negative.');
    }

    const precision = new BN(1_000_000_000);
    const slippageFactorFloat = (1 - slippage / 100) * 1_000_000_000;
    const slippageFactor = new BN(Math.floor(slippageFactorFloat));

    const minQuote = finalQuote.mul(slippageFactor).div(precision);

    return minQuote;
}
